import { httpClient } from './http-client';
import type { AuthResponse } from '../types/api';

/** Settings-page view of the caller's second factor. Carries nothing secret. */
export interface TwoFactorStatus {
  enabled: boolean;
  pending_confirmation: boolean;
  confirmed_at?: string;
  recovery_codes_remaining: number;
  recovery_codes_total: number;
}

/**
 * What the server hands back to set up an authenticator app.
 *
 * The QR arrives as a data: URI rendered server-side, so the secret never
 * passes through a third-party QR library in the browser.
 */
export interface TwoFactorEnrolment {
  otpauth_uri: string;
  secret: string;
  qr_data_uri: string;
}

/**
 * Freshly minted recovery codes.
 *
 * The only time these values exist outside the user's hands: the server keeps
 * digests, so a lost set can be replaced but never re-read.
 */
export interface RecoveryCodes {
  recovery_codes: string[];
}

/** A login that stopped at the password step because 2FA is enabled. */
export interface TwoFactorChallenge {
  two_factor_required: true;
  challenge_token: string;
  expires_in: number;
}

/**
 * Narrow a login response.
 *
 * The server returns either a session or a challenge, never both, so this is
 * the one place the client decides which flow it is in.
 */
export function isTwoFactorChallenge(
  response: AuthResponse | TwoFactorChallenge,
): response is TwoFactorChallenge {
  return (response as TwoFactorChallenge)?.two_factor_required === true;
}

const ENDPOINTS = {
  STATUS: '/2fa/status',
  ENROL: '/2fa/enrol',
  CONFIRM: '/2fa/enrol/confirm',
  RECOVERY: '/2fa/recovery-codes',
  // POST, not DELETE: disabling carries a verification code in the body.
  DISABLE: '/2fa/disable',
  VERIFY: '/auth/2fa/verify',
} as const;

export class TwoFactorService {
  /** Current state of the caller's second factor. */
  async status(): Promise<TwoFactorStatus> {
    return httpClient.get<TwoFactorStatus>(ENDPOINTS.STATUS);
  }

  /**
   * Begin enrolment.
   *
   * Nothing is switched on by this call — the factor stays off until a code
   * from the returned secret is confirmed.
   */
  async beginEnrolment(): Promise<TwoFactorEnrolment> {
    return httpClient.post<TwoFactorEnrolment>(ENDPOINTS.ENROL);
  }

  /** Prove the authenticator holds the secret, and switch the factor on. */
  async confirmEnrolment(code: string): Promise<RecoveryCodes> {
    return httpClient.post<RecoveryCodes>(ENDPOINTS.CONFIRM, { code });
  }

  /** Replace every recovery code. Requires a live TOTP or recovery code. */
  async regenerateRecoveryCodes(code: string): Promise<RecoveryCodes> {
    return httpClient.post<RecoveryCodes>(ENDPOINTS.RECOVERY, { code });
  }

  /** Turn the factor off. Requires a live TOTP or recovery code. */
  async disable(code: string): Promise<{ message: string }> {
    return httpClient.post<{ message: string }>(ENDPOINTS.DISABLE, { code });
  }

  /** Exchange a challenge and a code for a real session. */
  async verifyChallenge(challengeToken: string, code: string): Promise<AuthResponse> {
    return httpClient.post<AuthResponse>(ENDPOINTS.VERIFY, {
      challenge_token: challengeToken,
      code,
    });
  }
}

export const twoFactorService = new TwoFactorService();
